import React, { FC, useEffect, useState } from 'react';
import { UploadOutlined } from '@ant-design/icons';
import { Col, DatePickerProps, Form, message, Row, Select, Upload, UploadFile, UploadProps } from 'antd';
import { RcFile } from 'antd/es/upload';
import { api, endpoints } from '../../../../api';
import {
  CloseButton,
  CustomButton,
  CustomDatePicker,
  CustomSelect,
  FileLinkLayout,
  FormBlock,
  FormItem,
  GridItem,
} from '../../../../styles/styled';
import { getBase64 } from '../../../../utils/convertToBase64';
import { ExpertisesLineOfTextbooksType } from '../../../ExpertisePage/types';
import { BooksType, BookType, PublishersType, SchoolClassesType } from '../../../NewApplicationPage/types';
import { NmsMeetingSearchType, NmsTextbookGroupsType } from '../../types';

const { Option } = Select;

type NmsMeetingMaterialProps = {
  removeMaterial: () => void;
  changeMaterial: (material: NmsTextbookGroupsType, id: number) => void;
  school_classes: SchoolClassesType;
  publishers: PublishersType;
  material: NmsTextbookGroupsType;
  editing?: boolean;
  setLastLoaded?: () => void;
  mat_id: number;
  setOpenBook: (value: string) => void;
  addRemovableFile: (id: number) => void;
};

export const NmsMeetingMaterial: FC<NmsMeetingMaterialProps> = ({
  school_classes,
  publishers,
  changeMaterial,
  removeMaterial,
  mat_id,
  editing,
  material,
  setOpenBook,
  addRemovableFile,
}) => {
  const [textbooksState, setTextbooksState] = useState<BooksType | null>();
  const [searchInput, setSearchInput] = useState<NmsMeetingSearchType>({});
  const [currentTextbookState, setCurrentTextbook] = useState<BookType | undefined>(undefined);
  const [fileList, setFileList] = useState<UploadFile[]>([]);

  const onChange: DatePickerProps['onChange'] = (date, dateString) => {
    setSearchInput({ ...searchInput, date: dateString.toString().split('-').reverse().join('-') as string });
  };

  const searchBook = ({
    publisher,
    school_class,
    date,
  }: {
    publisher?: number;
    school_class?: number;
    date?: string;
  }) => {
    api
      .get<ExpertisesLineOfTextbooksType>(
        endpoints.getTextbooks({ perPage: 1000, publisher: publisher, school_classes: school_class, date: date }),
      )
      .then((el) => setTextbooksState(el.data.textbooks))
      .catch(() => message.error('Произошла ошибка при загрузке данных'));
  };

  useEffect(() => {
    if (editing)
      setFileList(
        material?.files?.map((el, idx) => {
          return { name: el?.file_path?.split('/').pop() || '', uid: `${idx}`, id: idx };
        }),
      );

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editing]);

  useEffect(() => {
    if (editing && material?.textbook?.id)
      api.get<BookType>(endpoints.getTextbookById(material.textbook.id)).then((el) => setCurrentTextbook(el.data));

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editing]);

  const loadBook = (id: number) => {
    api
      .get<BookType>(endpoints.getTextbookById(id))
      .then((el) => {
        setCurrentTextbook(el.data);
        changeMaterial({ ...material, textbook: { id: el.data.id || 0 } }, mat_id);
      })
      .catch(() => message.error('Ошибка при загрузке данных'));
  };

  const props: UploadProps = {
    name: 'file',
    accept: '.pdf',
    fileList: fileList.map((el) => {
      el.status = 'done';

      return el;
    }),
    beforeUpload: (file) => {
      const isPNG = file.type === 'application/pdf';

      return isPNG || Upload.LIST_IGNORE;
    },
    onChange(info) {
      setFileList(info.fileList);

      if (info.file.status === 'removed' && material?.files.find((el) => el?.file_path?.includes(info.file.name)))
        addRemovableFile(material?.files.find((el) => el?.file_path?.includes(info.file.name))?.id || 0);

      if (info.fileList.length === 0) {
        setFileList([]);

        changeMaterial({ ...material, files: [] }, mat_id);
      }

      if (info.file.status === 'uploading' && fileList.length !== material.files.length)
        getBase64(info.file.originFileObj as RcFile, (url) => {
          api
            .post<{ file_id: number }>(endpoints.addFile(), {
              base64: url.split('base64,')[1],
              title: info.file.name as string,
              ext: 'pdf',
            })
            .then((el) => changeMaterial({ ...material, files: [...material.files, { id: el.data.file_id }] }, mat_id));
        });
    },
  };

  return (
    <FormBlock padding="60px 30px 20px 30px" noBorderRadius position="relative">
      <Form>
        <Row gutter={[20, 20]}>
          <Col span={8}>
            <FormItem>
              <div className="text">Дата регистрации заявления</div>
              <CustomDatePicker onChange={onChange} format="DD.MM.YYYY" />
            </FormItem>
          </Col>
          <Col span={8}>
            <FormItem redstar>
              <div className="text">Издательство</div>
              <CustomSelect
                onChange={(value, option) => {
                  setSearchInput({ ...searchInput, publisher: parseInt(Object.values(option)[0]) });
                }}
                value={searchInput.publisher ? publishers.find((el) => el.id === searchInput.publisher)?.title : 'Все'}
                defaultValue="Все"
                showSearch
              >
                <Option key="0" value={0}>
                  Все
                </Option>
                {publishers.map((el) => (
                  <Option key={el.id} value={el.title}>
                    {el.title}
                  </Option>
                ))}
              </CustomSelect>
            </FormItem>
          </Col>
          <Col span={4}>
            <FormItem>
              <div className="text">Класс</div>
              <CustomSelect
                onChange={(value, option) => {
                  setSearchInput({
                    ...searchInput,
                    school_classes: parseInt(Object.values(option)[0]),
                  });
                }}
                value={
                  searchInput?.school_classes
                    ? school_classes.find((el) => parseInt(el.id) === searchInput.school_classes)?.title
                    : 'Все'
                }
                showSearch
              >
                <Option value="Все">Все</Option>
                {school_classes &&
                  school_classes.map((el) => {
                    return (
                      <Option value={el.id} key={el.id}>
                        {el.title}
                      </Option>
                    );
                  })}
              </CustomSelect>
            </FormItem>
          </Col>
          <Col span={4}>
            <br />
            <CustomButton
              onClick={() =>
                searchBook({
                  publisher: searchInput?.publisher,
                  school_class: searchInput.school_classes,
                  date: searchInput.date,
                })
              }
              width="100%"
              padding="8px 10px"
            >
              применить
            </CustomButton>
          </Col>
          <Col span={24}>
            <FormItem redstar>
              <div className="text">Наименование учебника</div>
              <CustomSelect
                showSearch
                value={currentTextbookState && currentTextbookState.title}
                onChange={(value) =>
                  setCurrentTextbook(textbooksState?.find((el) => el.id === parseInt(value as string)))
                }
              >
                {textbooksState?.map((el) => (
                  <Option value={el.id} key={el.id}>
                    {el.title}
                  </Option>
                ))}
              </CustomSelect>
            </FormItem>
          </Col>
          {currentTextbookState && (
            <Col span={24}>
              <CustomButton width="200px" onClick={() => loadBook(currentTextbookState.id || 0)}>
                Загрузить
              </CustomButton>
            </Col>
          )}

          {currentTextbookState?.file_textbook?.file_path && (
            <Col span={24}>
              <GridItem direction="row" align="center" justify="flex-start" gap="10px">
                <FileLinkLayout
                  onClick={() =>
                    currentTextbookState?.file_textbook?.file_path &&
                    setOpenBook(currentTextbookState?.file_textbook?.file_path)
                  }
                >
                  Учебник.pdf
                </FileLinkLayout>
              </GridItem>
            </Col>
          )}
          {currentTextbookState?.file_instruction?.file_path && (
            <Col span={24}>
              <GridItem direction="row" align="center" justify="flex-start" gap="10px">
                <FileLinkLayout
                  onClick={() =>
                    currentTextbookState?.file_instruction?.file_path &&
                    setOpenBook(currentTextbookState?.file_instruction?.file_path)
                  }
                >
                  Инструкция по установке.pdf
                </FileLinkLayout>
              </GridItem>
            </Col>
          )}
          {currentTextbookState?.file_guideline?.file_path && (
            <Col span={24}>
              <GridItem direction="row" align="center" justify="flex-start" gap="10px">
                <FileLinkLayout
                  onClick={() =>
                    currentTextbookState?.file_guideline?.file_path &&
                    setOpenBook(currentTextbookState?.file_guideline?.file_path)
                  }
                >
                  Методическое пособие.pdf
                </FileLinkLayout>
              </GridItem>
            </Col>
          )}
          {currentTextbookState?.text_book_edu_man?.map(
            (el) =>
              el.file?.file_path && (
                <Col span={24}>
                  <GridItem direction="row" align="center" justify="flex-start" gap="10px">
                    <FileLinkLayout onClick={() => el.file?.file_path && setOpenBook(el.file.file_path)}>
                      {`Учебное пособие ${el.title}`}
                    </FileLinkLayout>
                  </GridItem>
                </Col>
              ),
          )}

          {currentTextbookState && material.textbook && (
            <Upload {...props}>
              <CustomButton flex lined width="400px">
                <div className="icon">
                  <UploadOutlined />
                </div>
                Загрузить дополнительные материалы
              </CustomButton>
            </Upload>
          )}
        </Row>
      </Form>
      <div className="close-button">
        <CloseButton onClick={() => removeMaterial()}>+</CloseButton>
      </div>
    </FormBlock>
  );
};
